import { Component, Input, OnChanges, OnInit, SimpleChanges, ViewChild } from '@angular/core';
import { Chart, ChartConfiguration, ChartType } from 'chart.js';
import { BaseChartDirective } from 'ng2-charts';

import {default as Annotation} from 'chartjs-plugin-annotation';
import { Match } from '../../_shared/models/match.model';

import { MomentumChartService } from './momentum-chart.service';
import { ThemeService } from '../../_core/services/theme.service';
import { ScoreboardService } from '../../scoreboard/scoreboard.service';
import { Game } from '../../_shared/models/game.model';
import { Point } from '../../_shared/models/point.model';
import { Set } from '../../_shared/models/set.model';

@Component({
  standalone: false,
  selector: 'app-momentum-chart',
  templateUrl: './momentum-chart.component.html',
  styleUrls: ['./momentum-chart.component.scss'],
})
export class MomentumChartComponent implements OnInit, OnChanges {
  @Input() match: Match;
  @ViewChild(BaseChartDirective) chart?: BaseChartDirective;

  pointDataset: { x:number, y: number }[];
  momentumData: {
    momentumValue: number,
    set?: Set,
    game?: Game,
    point?: Point
  }[];

  isChartActive: boolean;
  chartHeight: number;
  yAxisLabels: string[] = ['0-0']
  colors: { gridColor: string; side1Color?: string; side2Color?: string; } = {
    gridColor: '#dfdfdf'
  };

  public lineChartType: ChartType = 'line';
  public lineChartData: ChartConfiguration['data'] = {
    datasets: [
      {
        data: [],
        borderWidth: 1,
        // backgroundColor: 'rgba(148,159,177,0.2)',
        // borderColor: 'rgba(148,159,177,1)',
        pointBackgroundColor: ctx => {
          if (this.momentumData[this.momentumData.length - ctx.raw.y]?.point?.winningSide === 1) return this.colors.side1Color;
          else if (this.momentumData[this.momentumData.length - ctx.raw.y]?.point?.winningSide === 2) return this.colors.side2Color;
          return 'gray'
        },
        pointBorderColor: ctx => {
          if (this.momentumData[this.momentumData.length - ctx.raw.y]?.point?.winningSide === 1) return this.colors.side1Color;
          else if (this.momentumData[this.momentumData.length - ctx.raw.y]?.point?.winningSide === 2) return this.colors.side2Color;
          return 'gray'
        },
        fill: false,
        pointStyle: 'circle'
      }
    ],
    labels: []
  };

  public lineChartOptions: ChartConfiguration['options'] = {
    animation: false,
    elements: {
      line: {
        tension: 0
      }
    },
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      x: {
        position: 'top',
        min: -6,
        max: 6,
        ticks: {
          color: 'gray',
          stepSize: 1
        },
        grid: {
          color: this.colors.gridColor,
          lineWidth: context => {
            const index = this.lineChartData.labels.findIndex(l => l === 0);
            return context.tick.value === index ? 2 : 0
          }
        }
      },
      y: {
        position: 'left',
        beginAtZero: true,
        grid: {
          color: 'lightgray',
          lineWidth: 1
        },
        ticks: {
          color: 'gray',
          stepSize: 1,
          callback: (value) => {
            return this.yAxisLabels[value];
          }
        }
      }
    },

    plugins: {
      legend: { display: false },
      tooltip: {
        enabled: false
      },
      annotation: {
        annotations: { }
      }
    }
  };

  constructor(
    private momentumChartService: MomentumChartService,
    private themeService: ThemeService,
    private scoreboardService: ScoreboardService
  ) {
    Chart.register(Annotation)
  }

  ngOnInit() {
    this.getColors();
    this.getPointDataSet();
  }

  async ngOnChanges(change: SimpleChanges) {
    this.match = change.match.currentValue;

    if (this.match.sets.length) {
      this.getPointDataSet();
    }
  }

  private getPointDataSet() {
    let momentumValue = 0;
    this.pointDataset = [{x: 0, y: 0}];
    this.momentumData = [];
    this.yAxisLabels = ['0-0'];

    this.match.sets.forEach((set, i) => {
      set.games.forEach((game, j) => {
        game.points.forEach((point, k) => {

          // CALCUALTE MOMENTUM VALUE
          if (point.winningSide === 1) momentumValue--;
          else if (point.winningSide === 2) momentumValue++;
          // If users enters who one the game award 2 momentum points to the game winner
          else if (game.winningSide === 1) momentumValue -= 2;
          else if (game.winningSide === 2) momentumValue += 2;

          this.yAxisLabels.push(this.generateYAxisLabel(game, point, k));
          // this.yAxisLabels.push('');

          this.generateAnnotations(game, set, i, j, k);

          // Add point to the top of the chart
          this.pointDataset.unshift({ x: momentumValue, y: this.pointDataset[0].y + 1 });

          this.momentumData.unshift({
            momentumValue: momentumValue,
            set: set,
            game: game,
            point: point
          });
        });
      });
    });

    // Add 0 value for the start of the match
    if (this.pointDataset.length > 1) {
        this.momentumData.unshift({
          momentumValue: 0
        });
        this.pointDataset.push({ x: 0, y: 0 });

        if (this.match.matchStatus !== 'COMPLETED') {
          this.yAxisLabels[this.yAxisLabels.length - 1] = '';
        }
    }

    this.getMinMaxOnXAxis()

    this.isChartActive = false;
    setTimeout(() => {
      this.isChartActive = true;
    });

    this.lineChartData.datasets[0].data = this.pointDataset;

    this.updateHeight();

  }

  private getMinMaxOnXAxis() {
    // Ensure that chart is at least 6 steps wide
    let max = this.pointDataset.reduce((prev, current) => (prev.x > current.x) ? prev : current).x;
    let min = this.pointDataset.reduce((prev, current) => (prev.x < current.x) ? prev : current).x;

    if (!max || max < 6) {
      max = 6;
    }

    if (!min || min > -6) {
      min = -6;
    }

    this.lineChartOptions.scales.x.max = max;
    this.lineChartOptions.scales.x.min = min;


    // Add labels to x value. Everything from min to max X value
    let xLabelValue = min;
    this.lineChartData.labels = [];

    while (xLabelValue <= max) {
      this.lineChartData.labels.push(xLabelValue);
      xLabelValue++;
    }
  }

  private generateAnnotations(game: Game, set: Set, i: number, j: number, k: number) {
    if (this.match.winningSide && this.match.sets.length - 1 === i && j === set.games.length - 1 && k === game.points.length - 1) {
      this.lineChartOptions.plugins.annotation.annotations[this.pointDataset[0].y] =
        this.momentumChartService.getAnnotationObj('MATCH', this.pointDataset[0].y + 2 , this.match.winningSide, this.colors);

    } else if (set.winningSide && j === set.games.length - 1 && k === game.points.length - 1) {
      this.lineChartOptions.plugins.annotation.annotations[this.pointDataset[0].y] =
        this.momentumChartService.getAnnotationObj('SET', this.pointDataset[0].y + 1, set.winningSide, this.colors);

    } else if (game.winningSide && game.points.length === k + 1) {
      this.lineChartOptions.plugins.annotation.annotations[this.pointDataset[0].y] =
        this.momentumChartService.getAnnotationObj('GAME', this.pointDataset[0].y + 1, game.winningSide, this.colors);
    }
  }

  private generateYAxisLabel(game: Game, point: Point, k: number): string {
    return game.points.length === k + 1
      ? game.side1Score + '-' + game.side2Score
      : this.momentumChartService.getPointScore(1, point, game) + '-' + this.momentumChartService.getPointScore(2, point, game);
  }

  private updateHeight() {
    this.chartHeight = this.lineChartData.datasets[0].data.length * 20;
  }

  private getColors() {

    this.colors = {
      gridColor: this.themeService.isDarkMode ? '#363636' : '#dfdfdf',
      side1Color: this.scoreboardService.getColor(1, null),
      side2Color: this.scoreboardService.getColor(2, null)
    }

    this.lineChartOptions.scales.x.grid.color = this.colors.gridColor;
    this.lineChartOptions.scales.y.grid.color = this.colors.gridColor;
  }
}

